import type { SsdSaleFact } from "@/lib/ssdSalesClient";

export function formatAmount(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function matchablePct(matchable: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((matchable / total) * 100)}%`;
}

export function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.map((s) => s.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

export const QUALITY_ISSUE_LABELS: Record<string, string> = {
  invalid_email: "Invalid email",
  invalid_phone: "Invalid phone",
  missing_email: "Missing email",
  missing_phone: "Missing phone",
  missing_zip: "Missing ZIP",
  duplicate_email: "Duplicate email",
  duplicate_phone: "Duplicate phone",
  duplicate_source_id: "Duplicate sale id",
};

export function inboundEventLabel(eventType: string): string {
  return eventType.replace(/_/g, ".");
}

export function qualityIssueLabel(issueType: string): string {
  return QUALITY_ISSUE_LABELS[issueType] ?? issueType.replace(/_/g, " ");
}

export const QUALITY_PAGE_SIZES = [5, 10, 25] as const;
export type QualityPageSize = (typeof QUALITY_PAGE_SIZES)[number];

export function paginateRows<T>(
  rows: T[],
  page: number,
  pageSize: number
): { items: T[]; page: number; pages: number } {
  const size = pageSize > 0 ? pageSize : 10;
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const p = Math.min(Math.max(1, page), pages);
  const start = (p - 1) * size;
  return { items: rows.slice(start, start + size), page: p, pages };
}

export function filterQualityIssues<T extends { issue_type: string }>(issues: T[], issueType: string): T[] {
  if (!issueType) return issues;
  return issues.filter((row) => row.issue_type === issueType);
}

export function filterAudienceFacts(
  facts: SsdSaleFact[],
  opts: { dealer: string; status: string; matchableOnly: boolean; newOnly: boolean; q: string }
): SsdSaleFact[] {
  const q = opts.q.trim().toLowerCase();
  return facts.filter((row) => {
    if (opts.dealer && row.source_dealer_name !== opts.dealer) return false;
    if (opts.status && row.status !== opts.status) return false;
    if (opts.matchableOnly && !(row.email_valid || row.phone_valid)) return false;
    if (opts.newOnly && !row.is_new) return false;
    if (q) {
      const hay = `${row.customer_full_name} ${row.customer_email} ${row.customer_phone} ${row.order_number ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}
